import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { PageSEO } from '@/components/seo/SEOHelmet';
import { t } from '@/i18n/translations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, Eye, EyeOff, User } from 'lucide-react';

// Google Icon Component
const GoogleIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
    </svg>
);

const SignupPage = () => {
    const { register, loginWithGoogle, isAuthenticated, loading: authLoading } = useAuth();
    const { language, getLocalizedPath } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const lang = language || 'en';

    const [email, setEmail] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Get redirect URL from query params
    const searchParams = new URLSearchParams(location.search);
    const redirectTo = searchParams.get('redirect') || '/';

    // Redirect if already logged in
    useEffect(() => {
        if (isAuthenticated && !authLoading) {
            navigate(getLocalizedPath(redirectTo));
        }
    }, [isAuthenticated, authLoading, navigate, getLocalizedPath, redirectTo]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validation
        if (password !== confirmPassword) {
            setError(t('auth.passwordMismatch', lang));
            return;
        }

        if (password.length < 8) {
            setError(t('auth.passwordRequirements', lang));
            return;
        }

        setLoading(true);

        try {
            await register(email, username, password, confirmPassword);
            toast.success(t('auth.registerSuccess', lang));
            navigate(getLocalizedPath(redirectTo));
        } catch (err) {
            const errorMsg = err.response?.data?.detail || err.message || 'Registration failed';
            
            // Map common errors to translations
            if (errorMsg.includes('Email already')) {
                setError(t('auth.emailExists', lang));
            } else if (errorMsg.includes('Username already')) {
                setError(t('auth.usernameExists', lang));
            } else {
                setError(errorMsg);
            }
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = () => {
        loginWithGoogle(redirectTo);
    };

    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
                <Loader2 className="h-8 w-8 animate-spin text-[#6A1F2E]" />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-4 py-12">
            <PageSEO title="Sign Up" description="Create your Sous Chef Linguine account." path="/signup" noIndex={true} />
            <Card className="w-full max-w-md border-[#E8E4DC]">
                <CardHeader className="text-center">
                    <div className="mx-auto mb-4 w-12 h-12 bg-[#6A1F2E] rounded-full flex items-center justify-center">
                        <span className="text-white text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
                            SCL
                        </span>
                    </div>
                    <CardTitle className="text-2xl" style={{ fontFamily: 'var(--font-heading)' }}>
                        {t('auth.createAccount', lang)}
                    </CardTitle>
                    <CardDescription>
                        {t('auth.signUp', lang)}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {/* Google Login Button */}
                    <Button
                        variant="outline"
                        className="w-full h-11 mb-6 border-[#E8E4DC] hover:bg-[#F5F2EC]"
                        onClick={handleGoogleLogin}
                        disabled={loading}
                    >
                        <GoogleIcon />
                        <span className="ml-2">{t('auth.continueWithGoogle', lang)}</span>
                    </Button>

                    {/* Divider */}
                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                            <Separator className="w-full" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-white px-2 text-[#7C7C7C]">
                                {t('auth.orContinueWith', lang)}
                            </span>
                        </div>
                    </div>

                    {/* Registration Form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">{t('auth.email', lang)}</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7C7C7C]" />
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    disabled={loading}
                                    className="pl-10 border-[#E8E4DC] focus:border-[#6A1F2E] focus:ring-[#6A1F2E]/20"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="username">{t('auth.username', lang)}</Label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7C7C7C]" />
                                <Input
                                    id="username"
                                    type="text"
                                    placeholder="your_username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                                    required
                                    minLength={3}
                                    maxLength={30}
                                    disabled={loading}
                                    className="pl-10 border-[#E8E4DC] focus:border-[#6A1F2E] focus:ring-[#6A1F2E]/20"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">{t('auth.password', lang)}</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7C7C7C]" />
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={8}
                                    disabled={loading}
                                    className="pl-10 pr-10 border-[#E8E4DC] focus:border-[#6A1F2E] focus:ring-[#6A1F2E]/20"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7C7C7C] hover:text-[#2C2C2C]"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            <p className="text-xs text-[#7C7C7C]">
                                {t('auth.passwordRequirements', lang)}
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">{t('auth.confirmPassword', lang)}</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7C7C7C]" />
                                <Input
                                    id="confirmPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    disabled={loading}
                                    className="pl-10 border-[#E8E4DC] focus:border-[#6A1F2E] focus:ring-[#6A1F2E]/20"
                                />
                            </div>
                        </div>

                        {error && (
                            <p className="text-sm text-red-600">{error}</p>
                        )}

                        <Button 
                            type="submit" 
                            className="w-full h-11 bg-[#6A1F2E] hover:bg-[#8B2840]"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {t('auth.signUp', lang)}...
                                </>
                            ) : (
                                t('auth.signUp', lang)
                            )}
                        </Button>
                    </form>

                    {/* Login Link */}
                    <p className="mt-6 text-center text-sm text-[#7C7C7C]">
                        {t('auth.haveAccount', lang)}{' '}
                        <Link 
                            to={getLocalizedPath(`/login${redirectTo !== '/' ? `?redirect=${encodeURIComponent(redirectTo)}` : ''}`)}
                            className="text-[#6A1F2E] font-medium hover:underline"
                        >
                            {t('auth.login', lang)}
                        </Link>
                    </p>
                </CardContent>
            </Card>
        </div>
    );
};

export default SignupPage;
